import {Box, Button, Popover, Stack, Typography} from "@mui/material";
import {grey, lightBlue} from "@mui/material/colors";
import {
  DateCalendar,
  LocalizationProvider,
  PickersDay,
} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {IconCalendar,IconChevronDown, IconX} from "@tabler/icons-react";
import moment from "moment";
import {useEffect, useRef, useState} from "react";
import QuickRangeMenu, {QUICK_RANGES} from "./QuickRangeMenu";

// ─── Range Day ───────────────────────────────────────────────────────────────

const RangeDay = ({
                    day,
                    rangeStart,
                    rangeEnd,
                    onDayClick,
                    onDayHover,
                    outsideCurrentMonth,
                    ...props
                  }) => {
  const isStart = rangeStart && !outsideCurrentMonth && day.isSame(rangeStart, "day");
  const isEnd = rangeEnd && !outsideCurrentMonth && day.isSame(rangeEnd, "day");

  const inRange =
    rangeStart &&
    rangeEnd &&
    !outsideCurrentMonth &&
    day.isAfter(rangeStart, "day") &&
    day.isBefore(rangeEnd, "day");

  const showBg = (isStart || isEnd || inRange) && rangeStart && rangeEnd;

  return (
    <Box
      onMouseEnter={() => !outsideCurrentMonth && onDayHover(day)}
      sx={{position: "relative", display: "flex", justifyContent: "center"}}
    >
      {showBg && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            height: 36,
            left: isStart ? "50%" : 0,
            right: isEnd ? "50%" : 0,
            bgcolor: lightBlue[50],
            zIndex: 0,
          }}
        />
      )}

      <PickersDay
        {...props}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        onClick={() => !outsideCurrentMonth && onDayClick(day)}
        selected={isStart || isEnd}
        sx={{
          zIndex: 1,
          ...(isStart || isEnd
            ? {
              bgcolor: `${lightBlue[500]} !important`,
              color: "white !important",
            }
            : {}),
        }}
      />
    </Box>
  );
};

// ─── Time Control ─────────────────────────────────────────────────────────────

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

const TimeControl = ({value, setValue, min, max}) => {
  const inc = () => setValue((v) => (v + 1 > max ? min : v + 1));
  const dec = () => setValue((v) => (v - 1 < min ? max : v - 1));

  const handleChange = (e) => {
    let val = parseInt(e.target.value.replace(/\D/g, ""), 10);
    if (isNaN(val)) val = min;
    setValue(clamp(val, min, max));
  };

  return (
    <Stack direction={"row"} alignItems="center" spacing={0.5}>

      <Typography sx={{cursor: "pointer"}} onClick={dec}>
        -
      </Typography>
      <input
        value={String(value).padStart(2, "0")}
        onChange={handleChange}
        style={{
          width: 30,
          textAlign: "center",
          border: "none",
          outline: "none",
          background: "transparent",
          fontWeight: 600,
        }}
      />
      <Typography sx={{cursor: "pointer"}} onClick={inc}>
        +
      </Typography>

    </Stack>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const DateRangePickerWithMenu = ({
                                   onStartChange,
                                   onEndChange,
                                   defaultLabel = null,
                                   withTime = false,
    onChange,triggerSx
                                 }) => {
  const defaultEntry = defaultLabel
    ? QUICK_RANGES.find((r) => r.label === defaultLabel)
    : null;

  // ─── Time state ───
  const [time, setTime] = useState({h: 0, m: 0, s: 0});

  const applyTime = (date) => {
    if (!date || !withTime) return date;
    return moment(date)
      .set({
        hour: time.h,
        minute: time.m,
        second: time.s,
      })
      .toDate();
  };

  // ─── Date state ───
  const [internalStart, setInternalStart] = useState(() => {
    if (!defaultEntry) return null;
    const [s] = defaultEntry.calc();
    return applyTime(s.toDate());
  });

  const [internalEnd, setInternalEnd] = useState(() => {
    if (!defaultEntry) return null;
    const [, e] = defaultEntry.calc();
    return applyTime(e.toDate());
  });

  const [selectedLabel, setSelectedLabel] = useState(defaultLabel);

  const setStart = (d) => {
    const v = applyTime(d);
    setInternalStart(v);
    onStartChange?.(v);
  };

  const setEnd = (d) => {
    const v = applyTime(d);
    setInternalEnd(v);
    onEndChange?.(v);
  };

  // ─── Sync time changes ───
  useEffect(() => {
    if (internalStart) setStart(internalStart);
    if (internalEnd) setEnd(internalEnd);
  }, [time]); // eslint-disable-line

  // ─── Picker logic ───
  const [anchorEl, setAnchorEl] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const calendarRef = useRef(null);

  const handleClose = () => {
    setAnchorEl(null);
    setSelecting(false);
    setAnchor(null);
  };

  const handleDayClick = (day) => {
    setSelectedLabel(null);

    if (!selecting) {
      setAnchor(day.clone());
      setSelecting(true);
      setStart(day.clone().startOf("day").toDate());
      setEnd(null);
    } else {
      const [start, end] = anchor.isBefore(day)
        ? [anchor, day]
        : [day, anchor];

        const s = start.clone().startOf("day");
        const e = end.clone().endOf("day");
        setStart(s.toDate());
        setEnd(e.toDate());
        onChange?.({
            label: `${s.format("DD MMM YYYY")} — ${e.format("DD MMM YYYY")}`,
            start: s.clone(),
            end: e.clone(),
        });
        handleClose();
    }
  };

  const handleQuickSelect = (label, start, end) => {
    setSelectedLabel(label);
    setStart(start);
    setEnd(end);
    onChange?.({label, start: moment(start), end: moment(end)});
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    const s = moment().startOf("day");
    const en = moment().endOf("day");
    setInternalStart(s.toDate());
    setInternalEnd(en.toDate());
    setSelectedLabel("Today");
    onStartChange?.(s.toDate());
    onEndChange?.(en.toDate());
    onChange?.({label: "Today", start: s, end: en});
  };

  let rangeStart = internalStart ? moment(internalStart) : null;
  let rangeEnd = internalEnd ? moment(internalEnd) : null;

  // Only the exact full-day "today" range (00:00 → 23:59) counts as the
  // default. Sub-day ranges (Last hour, Last 8 hours…) share today's date
  // but not its start/end minute, so they keep their own label.
  const isToday =
    internalStart && internalEnd &&
    moment(internalStart).isSame(moment().startOf("day"), "minute") &&
    moment(internalEnd).isSame(moment().endOf("day"), "minute");

  const label =
    internalStart && internalEnd && !isToday
      ? selectedLabel ||
      `${moment(internalStart).format("DD MMM YYYY")} — ${moment(
        internalEnd
      ).format("DD MMM YYYY")}`
      : "Select date range";

  return (
    <>
      {/* Trigger */}
      <Button
        size={"small"}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={triggerSx || {
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          textTransform: "none",
          color: "text.primary",
          bgcolor: grey[100],
        }}
      >
        <IconCalendar size={16} color={"#8A8A85"} strokeWidth={1.75}/>
        <Typography sx={{fontSize: "inherit"}} noWrap>{label}</Typography>
        {internalStart && internalEnd && !isToday ? (
          <IconX size={13} style={{opacity: 0.5}} onClick={handleClear}/>
        ) : (
          <IconChevronDown size={14} color={"#A3A39E"} strokeWidth={1.75}/>
        )}
      </Button>

      {/* Popover */}
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose} transitionDuration={0}>
        <Box sx={{display: "flex"}}>
          {/* Sidebar */}
          <QuickRangeMenu
            selected={selectedLabel}
            onSelect={handleQuickSelect}
            maxHeight={calendarRef.current?.offsetHeight ?? 340}
          />

          {/* Calendar */}
          <Box ref={calendarRef}>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
              <DateCalendar
                value={null}
                onChange={() => {
                }}
                reduceAnimations
                maxDate={moment()}
                referenceDate={moment()}
                slots={{day: RangeDay}}
                slotProps={{
                  day: () => ({
                    rangeStart,
                    rangeEnd,
                    onDayClick: handleDayClick,
                    onDayHover: () => {},
                  }),
                }}
              />
            </LocalizationProvider>
          </Box>

          {/* Time Panel */}
          {withTime && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                gap: 2,
                px: 4,
                alignItems: "center",
                justifyContent: "center",
                borderLeft: `1px solid ${grey[200]}`,
              }}
            >
              <Box>
                <Typography fontWeight={700} align={"center"}>Hour</Typography>
                <TimeControl
                  value={time.h}
                  setValue={(v) => setTime((t) => ({...t, h: v}))}
                  min={0}
                  max={23}
                />
              </Box>
              <Box>
                <Typography fontWeight={700} align={"center"}>Minute</Typography>
                <TimeControl
                  value={time.m}
                  setValue={(v) => setTime((t) => ({...t, m: v}))}
                  min={0}
                  max={59}
                />
              </Box>

              <Box>
                <Typography fontWeight={700} align={"center"}>Seconds</Typography>
                <TimeControl
                  value={time.s}
                  setValue={(v) => setTime((t) => ({...t, s: v}))}
                  min={0}
                  max={59}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default DateRangePickerWithMenu;